import { ReactNode, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';

import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { ModalCancelButton, ModalCloseButton, ModalWidth, ShowModalEventInterceptor } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { draftPrescriptionsTranslationKeys } from '../../../../../../translations';
import { DraftPrescriptionsFieldArray } from '../../DraftPrescriptionsForm.types';

import { PatientReimbursementIndicationsCheckModalContent } from './PatientReimbursementIndicationsCheckModalContent';
import {
   filterPrescriptionsWithoutValidReimbursementIndications,
   filterPrescriptionsWithoutValidReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements,
} from './patientReimbursementIndicationsCheck.utils';

type ContentRendererProps = {
   resolve: VoidFunction;
   reject: VoidFunction;
   closeModal: VoidFunction;
};
type ContentRenderer = (renderProps: ContentRendererProps) => JSX.Element;

type PatientEntitlementBenefitsCheckEventInterceptorProps = {
   readonly draftPrescriptions: DraftPrescriptionsFieldArray;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly children: ReactNode;
};

export const PatientReimbursementIndicationsCheckEventInterceptor = (
   props: PatientEntitlementBenefitsCheckEventInterceptorProps
): JSX.Element => {
   const { children, draftPrescriptions, visitDiagnoses } = props;

   const prescriptionsWithoutReimbursementIndications = useMemo(
      () => filterPrescriptionsWithoutValidReimbursementIndications(draftPrescriptions, visitDiagnoses),
      [draftPrescriptions, visitDiagnoses]
   );

   const prescriptionWithoutReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements = useMemo(
      () =>
         filterPrescriptionsWithoutValidReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements(
            draftPrescriptions,
            visitDiagnoses
         ),
      [draftPrescriptions, visitDiagnoses]
   );

   const allPrescriptionsWithoutReimbursementIndications = useMemo(
      () => [
         ...prescriptionsWithoutReimbursementIndications,
         ...prescriptionWithoutReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements,
      ],
      [
         prescriptionWithoutReimbursementIndicationsForStatutorilyDesignatedNonFullEntitlements,
         prescriptionsWithoutReimbursementIndications,
      ]
   );

   const content = useCallback<ContentRenderer>(
      ({ resolve, reject }) => (
         <>
            <PatientReimbursementIndicationsCheckModalContent
               prescriptionsWithoutIndications={allPrescriptionsWithoutReimbursementIndications}
               onConfirm={resolve}
            />
            <ModalCancelButton onClick={reject} />
            <ModalCloseButton onClick={reject} />
         </>
      ),
      [allPrescriptionsWithoutReimbursementIndications]
   );

   const shouldShowModal = protectFeature(
      'extendedMedicineDatabase',
      !isEmpty(allPrescriptionsWithoutReimbursementIndications),
      false
   );

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         title={draftPrescriptionsTranslationKeys.sendingRefundedPrescriptions}
         content={content}
         modalWidth={ModalWidth.NORMAL}
         testId={'reimbursementIndicationsWarning'}
         footerItemsHidden
      >
         {children}
      </ShowModalEventInterceptor>
   );
};
